import { Menu, MenuItem, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import { OverlayState } from '@/hooks/useOverlay'
import { useCreateWorkflowStateEvent } from '@/service-library/hooks/workflow-state-events'
import { WorkflowState } from '@/types/workflows'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { getSystemEventOptions } from '@/utils/workflow-state-events'

type AddSystemEventMenuProps = {
  overlay: OverlayState
  workflowState: WorkflowState
  listQueryKey: QueryKey
  existingCodes: string[]
}

export default function AddSystemEventMenu({
  overlay,
  workflowState,
  listQueryKey,
  existingCodes,
}: AddSystemEventMenuProps) {
  const { show_approvals } = useFeatureFlagContext()
  const { createWorkflowStateEvent } = useCreateWorkflowStateEvent({
    listQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to add event. Please try again later.')
    },
  })

  const options = getSystemEventOptions(existingCodes, !!show_approvals)

  function handleSelect(code: string, name: string) {
    createWorkflowStateEvent({
      id: generateUuid(),
      workflow_state_id: workflowState.id,
      name,
      actions: [],
      sort_order: 0,
      code,
      move_to_next_doc_in_validation: false,
      when_document_has_errors: 'trigger',
      allow_manual_trigger: false,
    })
    overlay.close()
  }

  return (
    <Menu
      anchorEl={overlay.anchorEl as Element}
      open={overlay.isOpen}
      onClose={overlay.close}
    >
      {options.map((event) => (
        <MenuItem
          key={event.code}
          disabled={event.isAdded}
          onClick={() => handleSelect(event.code, event.name)}
        >
          <Stack>
            <Typography>{event.name}</Typography>
            <Typography color="textSecondary" variant="caption">
              {event.description}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </Menu>
  )
}
